const analyticsUrl = import.meta.env.VITE_SUPABASE_URL?.trim()?.replace(/\/$/,'');
const analyticsKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();

const VISITOR_KEY = 'mielley_analytics_visitor_v1';
const SESSION_KEY = 'mielley_analytics_session_v1';

export type MielleyAnalyticsEventName =
  | 'page_view'
  | 'product_view'
  | 'search'
  | 'add_to_cart'
  | 'begin_checkout'
  | 'purchase'
  | 'whatsapp_click';

type AnalyticsPayload = {
  eventName:MielleyAnalyticsEventName;
  path?:string;
  searchTerm?:string;
  productSlug?:string;
  metadata?:Record<string,unknown>;
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string,unknown>>;
  }
}

const randomId=()=>{
  try{
    return crypto.randomUUID();
  }catch{
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

const readOrCreate=(storage:Storage,key:string)=>{
  try{
    const current=storage.getItem(key);
    if(current)return current;

    const next=randomId();
    storage.setItem(key,next);
    return next;
  }catch{
    return randomId();
  }
};

const cleanSearch=(value:string|undefined)=>{
  const text=String(value||'')
    .trim()
    .replace(/\s+/g,' ')
    .slice(0,120);

  if(!text)return '';

  // Do not send obvious email / Israeli mobile phone data
  if(
    /@/.test(text) ||
    /\b0?5\d[-\s]?\d{3}[-\s]?\d{4}\b/.test(text)
  ){
    return '[REDACTED]';
  }

  return text;
};

const device=()=>{
  const ua=navigator.userAgent||'';
  const width=window.innerWidth||screen.width||0;

  if(
    /iPad|Tablet|Android(?!.*Mobile)/i.test(ua) ||
    (width>=700&&width<=1100)
  ){
    return 'tablet';
  }

  if(
    /Mobi|iPhone|Android/i.test(ua) ||
    width<700
  ){
    return 'mobile';
  }

  return 'desktop';
};

const traffic=()=>{
  const params=new URLSearchParams(window.location.search);

  const utmSource=params.get('utm_source')||'';
  const utmMedium=params.get('utm_medium')||'';
  const utmCampaign=params.get('utm_campaign')||'';

  if(utmSource){
    return {
      source:utmSource,
      medium:utmMedium,
      campaign:utmCampaign,
    };
  }

  const ref=document.referrer||'';

  if(!ref){
    return {
      source:'Direct',
      medium:'direct',
      campaign:'',
    };
  }

  try{
    const host=new URL(ref).hostname.toLowerCase();

    if(host.includes('instagram')){
      return {source:'Instagram',medium:'social',campaign:''};
    }

    if(host.includes('facebook')||host.includes('fb.')){
      return {source:'Facebook',medium:'social',campaign:''};
    }

    if(host.includes('google')){
      return {source:'Google',medium:'organic',campaign:''};
    }

    if(host.includes('whatsapp')){
      return {source:'WhatsApp',medium:'messaging',campaign:''};
    }

    if(host===window.location.hostname){
      return {source:'Internal',medium:'internal',campaign:''};
    }

    return {
      source:host.replace(/^www\./,''),
      medium:'referral',
      campaign:'',
    };
  }catch{
    return {
      source:'Other',
      medium:'referral',
      campaign:'',
    };
  }
};

/* ---------------------------------------------------------
   GOOGLE TAG MANAGER / GA4 MIRROR
   --------------------------------------------------------- */

const pushToDataLayer=(payload:AnalyticsPayload)=>{
  if(typeof window==='undefined')return;
  if(window.location.pathname.startsWith('/admin'))return;

  const path=
    payload.path ||
    window.location.pathname+window.location.search;

  const productSlug=payload.productSlug||'';

  const eventMap:Partial<
    Record<MielleyAnalyticsEventName,string>
  > = {
    page_view:'mielley_page_view',
    product_view:'view_item',
    search:'search',
    add_to_cart:'add_to_cart',
    begin_checkout:'begin_checkout',
    purchase: 'purchase',
    whatsapp_click:'whatsapp_click',
  };

  
  

  const gaEvent=eventMap[payload.eventName];
  if(!gaEvent)return;

  const data:Record<string,unknown>={
    event:gaEvent,
    page_path:path,
  };

  // Product-view / add-to-cart fallback
  if(productSlug){
    data.items=[
      {
        item_id:productSlug,
      },
    ];
  }

  // Ecommerce information supplied by the page
  const metadata=payload.metadata||{};

  if(Array.isArray(metadata.items)){
    data.items=metadata.items;
  }

  if(
    typeof metadata.value==='number' &&
    Number.isFinite(metadata.value)
  ){
    data.value=metadata.value;
  }

  if(
    typeof metadata.currency==='string' &&
    metadata.currency.trim()
  ){
    data.currency=metadata.currency.trim();
  }

  if (
  typeof metadata.transaction_id === 'string' &&
  metadata.transaction_id.trim()
) {
  data.transaction_id = metadata.transaction_id.trim();
}

  if(payload.eventName==='search'){
    const searchTerm=cleanSearch(payload.searchTerm);

    if(searchTerm){
      data.search_term=searchTerm;
    }

    const resultsCount=
      Number(payload.metadata?.results_count);

    if(Number.isFinite(resultsCount)){
      data.results_count=resultsCount;
    }
  }

  window.dataLayer=window.dataLayer||[];
  window.dataLayer.push(data);
};

/* ---------------------------------------------------------
   MAIN TRACKER
   --------------------------------------------------------- */

export async function trackMielleyEvent(
  payload:AnalyticsPayload
){
  if(typeof window==='undefined')return;
  if(window.location.pathname.startsWith('/admin'))return;

  // Mirror event to GTM first
  pushToDataLayer(payload);

  // Supabase analytics may be unavailable,
  // but GTM should continue working.
  if(!analyticsUrl||!analyticsKey)return;

  const sessionId=readOrCreate(
    sessionStorage,
    SESSION_KEY
  );

  const visitorId=readOrCreate(
    localStorage,
    VISITOR_KEY
  );

  const t=traffic();

  const body={
    event_name:payload.eventName,
    session_id:sessionId,
    visitor_id:visitorId,

    path:
      payload.path ||
      window.location.pathname+window.location.search,

    referrer:document.referrer||null,

    source:t.source,
    medium:t.medium||null,
    campaign:t.campaign||null,

    device:device(),

    search_term:
      cleanSearch(payload.searchTerm)||null,

    product_slug:
      payload.productSlug||null,

    metadata:
      payload.metadata||{},
  };

  try{
    await fetch(
      `${analyticsUrl}/rest/v1/mielley_analytics_events`,
      {
        method:'POST',
        keepalive:true,

        headers:{
          apikey:analyticsKey,
          'Content-Type':'application/json',
          Prefer:'return=minimal',
        },

        body:JSON.stringify(body),
      }
    );
  }catch{}
}